import {
  FlagContent as ConfiguredFlagContent,
  type SponsorLogo as ConfiguredSponsorLogo,
  EventTypes,
  type TagCategoriesConfig,
} from '@racemap/sdk/schema/events';
import type { DeviceClasses } from '@racemap/sdk/schema/trackers';
import { UnitType } from '@racemap/utilities/types/units';
import type { Immutable } from 'immer';
import { memoize } from 'lodash';
import screenfull from 'screenfull';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import type ChunkManager from './ChunkManager';
import type { PlayerColor } from './PlayerColor';
import { actions } from './actions';
import type { Tuple3 } from './constants';
import { getCurrentEvents, reduceAny } from './selectors';
import type TrackView from './utils/TrackView';

export enum MapType {
  SATELLITE = 'SATELLITE',
  TERRAIN = 'TERRAIN',
  HYBRID = 'HYBRID',
  ROADMAP = 'ROADMAP',
  DARK = 'DARK',
}
export enum TimeMode {
  RELATIVE = 'RELATIVE',
  ABSOLUTE = 'ABSOLUTE',
}
export enum GeolocationStatus {
  DISABLED = 'DISABLED',
  OFF = 'OFF',
  SEARCHING = 'SEARCHING',
  FOLLOWING = 'FOLLOWING',
}

export type XY = {
  x: number;
  y: number;
};

export type LatLng = {
  lat: number;
  lng: number;
};

export type LatLngOpacity = {
  lat: number;
  lng: number;
  opacity: number;
};

export type Point = LatLng & {
  time: number;
  offset: number;
};

export type PointOpacity = LatLng & {
  time: number;
  opacity: number;
  offset: number;
};

type Color = PlayerColor;

export enum InfoSheetTabs {
  INFO = 'INFO',
  ELEVATION = 'ELEVATION',
  FLOW = 'FLOW',
  STATS = 'STATS',
  TIMING = 'TIMING',
}

export enum PinToShadowTrackMode {
  NONE = 'NONE',
  MAGNETIC = 'MAGNETIC',
  FORCED = 'FORCED',
}

export type StarterType = {
  readonly type: 'STARTER';
  readonly index: number;
  readonly id: string;
  readonly eventIndex: number;
  readonly name?: string;
  readonly startNumber?: string;
  readonly searchText: string;
  readonly markerColor: Color;
  readonly deviceClass: DeviceClasses | null;
  readonly tags: Map<string, string>;
};

export type PoiType = {
  readonly type: 'POI';
  readonly index: number;
  readonly id: string;
  readonly eventIndex: number;
  readonly name: string;
  readonly shortName?: string;
  readonly icon?: string;
  readonly markerColor: Color;
  readonly latlng: LatLng;
  readonly offset?: number;
};

export type TrackType = {
  readonly type: 'TRACK';
  readonly index: number;
  readonly id: string;
  readonly eventIndex: number;
  readonly name: string;
  readonly markerColor: Color;
  readonly coordinates: Array<Tuple3>;
  readonly lineWidth: number;
};

export type ExternalLayerType = {
  readonly type: 'EXTERNAL_LAYER';
  readonly index: number;
  readonly id: string;
  readonly eventIndex: number;
  readonly layerType: string;
  readonly tiles: Array<string>;
  readonly tileSize: number;
};

type StarterMap = Map<string, Array<StarterType>>;

/**
 * Draw-time flag content. A superset of the backend's FlagContent: the two
 * abbreviation modes are player-only and are reachable through the URL hash
 * (see HashManager) and the settings menu, never through the event config.
 */
export enum FlagContent {
  STARTNUMBER = 'STARTNUMBER',
  NAME = 'NAME',
  STARTNUMBER_AND_NAME = 'STARTNUMBER_AND_NAME',
  ONE_LETTER_NAMES = 'ONE_LETTER_NAMES',
  TWO_LETTER_NAMES = 'TWO_LETTER_NAMES',
}

/**
 * The configured FlagContent and the player's draw-time one are separate enums
 * with matching string values but different member names, so they are not
 * directly assignable — map explicitly instead of casting.
 */
export function toPlayerFlagContent(
  flagContent: ConfiguredFlagContent | null | undefined,
): FlagContent {
  switch (flagContent) {
    case ConfiguredFlagContent.NAME:
      return FlagContent.NAME;
    case ConfiguredFlagContent.STARTNUMBER_AND_NAME:
      return FlagContent.STARTNUMBER_AND_NAME;
    default:
      return FlagContent.STARTNUMBER;
  }
}

export type EventState = {
  readonly eventId: string;
  readonly index: number;
  readonly name: string;
  readonly shortName: string;
  readonly slug: string;
  readonly startTime: number;
  readonly endTime: number;
  readonly eventDescription: string;
  readonly eventWebsite: string;
  readonly eventContactMail: string;
  readonly eventLocation: string;
  readonly starters: Array<StarterType>;
  readonly startersCount: number;
  readonly pois: Array<PoiType>;
  readonly tracks: Array<TrackType>;
  readonly externalLayers: Array<ExternalLayerType>;
  readonly chunkedShadowTrack: TrackView | null;
  readonly shadowTrackIsVisible: boolean;
  /** Which of `tracks` is the shadowtrack, or null when the event has none. */
  readonly shadowTrackId: string | null;
  readonly modules: Modules;
  readonly updatedAt: Date;
  readonly shadowgeojsonHash: string | null;
  readonly options: {
    readonly markerTimeout: number;
    readonly stepWidth: number;
    readonly flagContent: FlagContent;
    readonly selectedFlagContent: FlagContent;
    readonly liveDelay: number;
    readonly pollingInterval: number;
    readonly replaySpeedup: number;
    readonly tailTimeout: number;
    readonly elevationChartOptions: null | {
      readonly min: null | number;
      readonly max: null | number;
    };
    readonly listOpenByDefault: boolean;
    readonly infoTabs: {
      readonly openByDefault: boolean;
      readonly defaultTab: InfoSheetTabs;
      readonly disabledTabs: Array<InfoSheetTabs>;
    };
    readonly viewport: null | {
      readonly latitude: number;
      readonly longitude: number;
      readonly zoom: number;
      readonly pitch: number;
      readonly bearing: number;
    };
    readonly showDistanceMarkers: boolean;
    readonly reloadInterval: number;
    readonly tagCategoriesConfig: Immutable<TagCategoriesConfig>;
  };
};

export type EventChangeableState = {
  readonly eventId: string;
  readonly chunkManager: ChunkManager;
  readonly starterTracks: Map<string, Array<Point>>;
  readonly shadowgeojsonHash: string | null;
  readonly startTime: number;
  readonly endTime: number;
  readonly netReplay: NetReplayModule;
};

export type ChunkInfo = {
  readonly id: string;
  readonly startTime: number;
  readonly endTime: number;
  readonly compressedBuffer: ArrayBuffer;
  readonly encodedLength: number;
  readonly decodedLength: number;
};

export type TimerState = {
  readonly speed: number;
  readonly time: number;
  readonly isLive: boolean;
  readonly isPlaying: boolean;
  readonly selected: Array<string>;
  readonly selectedTags: Map<string, Array<string>>;
  readonly defaultSelectedTags: Map<string, Array<string>>;
  readonly hidden: Set<string>;
  readonly highlighted: string | null;
  readonly highlightedOffset: number;
  readonly geolocation: {
    readonly status: GeolocationStatus;
    readonly latlng: null | LatLngOpacity;
  };
};

// Comes straight from the event schema — the player used to redeclare it
// with `logo` required, which it never is.
export type SponsorLogo = ConfiguredSponsorLogo;

type SponsorModule =
  | {
      readonly enabled: false;
    }
  | {
      readonly enabled: true;
      readonly alignment: 'left' | 'center' | 'right' | 'space-between' | 'space-around';
      readonly logos: Array<SponsorLogo> | null;
    };

type TimingModule =
  | { readonly enabled: false }
  | { readonly enabled: true; readonly leaderboardUrl: string };
export type NetReplayModule =
  | { readonly enabled: false }
  | {
      readonly enabled: true;
      readonly syncOffset: number;
      readonly syncTime: Date;
      readonly sliderStartTime: Date;
      readonly sliderEndTime: Date;
    };

type Modules = {
  readonly timing: TimingModule;
  readonly sponsor: SponsorModule;
  readonly netReplay: NetReplayModule;
  readonly projection: { enabled: boolean };
};

type OptionsState = {
  readonly type: EventTypes;
  readonly mapType: MapType;
  readonly fullscreenButton: boolean;
  readonly showAllFlags: boolean;
  readonly overrideFlagContent: null | FlagContent;
  readonly overrideSelectedFlagContent: null | FlagContent;
  readonly largeMode: boolean;
  readonly mapOnly: boolean;
  readonly overrideZoom: null | number;
  readonly overrideViewport: null | { longitude: number; latitude: number; zoom: number };
  readonly elevationShowProgress: null | boolean;
  readonly elevationShowGrid: null | boolean;
  readonly elevationShowAxis: null | boolean;
  readonly elevationShowPOI: null | boolean;
  readonly unitType: UnitType;
  readonly timeMode: TimeMode;
  readonly pinToShadowTrack: PinToShadowTrackMode;
};

export type State = {
  readonly eventId: string;
  readonly currentEventIndices: Array<number>;
  readonly events: Array<EventState>;
  readonly eventsChangeable: Array<EventChangeableState>;
  readonly startersById: StarterMap;
  readonly timer: TimerState;
  readonly options: OptionsState;
  readonly isReloading: boolean;
  /**
   * The camera currently on screen, with `zoom` normalised to a 1000px wide map. Recorded by Map so
   * the share dialog can put it into a link.
   */
  readonly currentViewport: null | { longitude: number; latitude: number; zoom: number };
  readonly actions: ReturnType<typeof actions>;
};

export const useStore = create<State>(
  combine(
    {
      eventId: '',
      currentEventIndices: [],
      events: [],
      eventsChangeable: [],
      startersById: new Map(),
      timer: {
        speed: 1,
        time: 0,
        isLive: false,
        isPlaying: false,
        selected: [],
        selectedTags: new Map(),
        hidden: new Set(),
        highlighted: null,
        highlightedOffset: -1,
        geolocation: {
          status:
            typeof window !== 'undefined' && 'geolocation' in window.navigator
              ? GeolocationStatus.OFF
              : GeolocationStatus.DISABLED,
          latlng: null,
        },
        defaultSelectedTags: new Map(),
      },
      options: {
        type: EventTypes.REGULAR,
        fullscreenButton: screenfull.isEnabled,
        mapType: MapType.TERRAIN,
        unitType: UnitType.METRIC,
        timeMode: TimeMode.RELATIVE,
        largeMode: false,
        mapOnly: false,
        overrideZoom: null,
        overrideViewport: null,
        showAllFlags: false,
        overrideFlagContent: null,
        overrideSelectedFlagContent: null,
        pinToShadowTrack: PinToShadowTrackMode.NONE,
        elevationShowProgress: null,
        elevationShowGrid: null,
        elevationShowAxis: null,
        elevationShowPOI: null,
      },
      isReloading: false,
      currentViewport: null,
    } as Omit<State, 'actions'>,
    memoize((set) => ({
      actions: actions(set),
    })),
  ),
);

export const useSponsorModule = () =>
  useStore((state) => reduceAny(getCurrentEvents(state), (a) => a.modules.sponsor));
